import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import SellerCarousel from "@src/components/elements/sellerpage/SellerCarousel";
import { SellerFooter } from "@src/pages/seller/SellerContainer";

const SellerAppContainer = () => {
  const appMockups = [
    "/images/seller_carousel/img01.jpg",
    "/images/seller_carousel/img02.jpg",
    "/images/seller_carousel/img03.jpg",
    "/images/seller_carousel/img04.jpg",
    "/images/seller_carousel/img05.jpg",
    "/images/seller_carousel/img06.jpg",
    "/images/seller_carousel/img07.jpg",
  ];

  const slides = [
    { type: "video" as const, src: "/images/seller_carousel/intro.mp4" },
    ...appMockups.map(img => ({ type: "image" as const, src: img })),
  ];

  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-8 bg-gray-50 pb-6">
      <div className="sticky top-0 z-10 w-full bg-white shadow">
        <nav className="flex justify-end gap-4 border-b py-4 pr-6">
          <Link to="/seller" className="px-3 py-2 font-medium text-gray-700 hover:text-blue-600">
            {t("seller.subTabs.overview")}
          </Link>
          <Link
            to="/seller/conditions"
            className="px-3 py-2 font-medium text-gray-700 hover:text-blue-600"
          >
            {t("seller.subTabs.Conditions")}
          </Link>
          <button className="border-b-2 border-blue-600 px-3 py-2 font-medium text-blue-600">
            {t("seller.subTabs.app")}
          </button>
        </nav>
      </div>

      <div className="flex w-full flex-wrap justify-center gap-12 px-4 md:px-8">
        <div className="max-w-[800px] text-center md:text-left">
          <h1 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">
            {t("seller.app.title")}
          </h1>
          <p className="mb-8 text-lg text-gray-600 md:text-xl">{t("seller.app.subtitle")}</p>

          <div className="mb-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center md:justify-start">
            <a
              href="https://apps.apple.com/de/app/tradefoox/id6477870055"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg bg-black px-6 py-3 text-white transition-colors hover:bg-gray-800"
            >
              <img src="/images/icons/apple-logo.svg" alt="Apple Store" className="h-8 w-8" />
              <div className="text-left">
                <div className="text-xs">{t("seller.app.downloadOnThe")}</div>
                <div className="text-sm font-semibold">App Store</div>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=app.tradefoox.mobile"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg bg-black px-6 py-3 text-white transition-colors hover:bg-gray-800"
            >
              <img src="/images/icons/playstore.svg" alt="Google Play" className="h-8 w-8" />
              <div className="text-left">
                <div className="text-xs">{t("seller.app.getItOn")}</div>
                <div className="text-sm font-semibold">Google Play</div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <SellerCarousel slides={slides} />

      <div className="flex w-full flex-wrap justify-center gap-12 px-4 md:px-8">
        <div className="max-w-[800px]">
          <h2 className="mb-6 text-3xl font-bold text-gray-900">
            {t("seller.app.features.title")}
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {(() => {
              // Pre-fetch all translations to ensure i18next detects them
              const managementTitle = t("seller.app.features.management.title");
              const managementDescription = t("seller.app.features.management.description");
              const salesTitle = t("seller.app.features.sales.title");
              const salesDescription = t("seller.app.features.sales.description");
              const messagingTitle = t("seller.app.features.messaging.title");
              const messagingDescription = t("seller.app.features.messaging.description");
              const notificationsTitle = t("seller.app.features.notifications.title");
              const notificationsDescription = t("seller.app.features.notifications.description");

              const features = [
                { title: managementTitle, description: managementDescription },
                { title: salesTitle, description: salesDescription },
                { title: messagingTitle, description: messagingDescription },
                { title: notificationsTitle, description: notificationsDescription },
              ];

              return features.map((feature, idx) => (
                <div key={idx} className="rounded-lg bg-white p-6 shadow-md">
                  <h3 className="mb-3 text-xl font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      <SellerFooter />
    </div>
  );
};

export default SellerAppContainer;
